/**
 * "Contracte → De rezolvat": the words for each case. Pure, so tested.
 *
 * The backend decides the category and the dates; here they become the
 * sentence an operator reads before picking up the phone. Times are shown on
 * Romania's clock, whatever the operator's computer says.
 */

const TZ = "Europe/Bucharest";

/** In the order they are read; urgent first. */
export const TODO_CATEGORIES = [
  { key: "urgent", label: "Urgent", tone: "bad", dot: "#C62828" },
  { key: "rejected", label: "Refuzat", tone: "warn", dot: "#D97706" },
  { key: "blocked", label: "Blocat", tone: "warn", dot: "#D97706" },
  { key: "no_reaction", label: "Fără reacție", tone: "warn", dot: "#CA8A04" },
  { key: "contacted", label: "Contactat — așteaptă semnătura", tone: "neutral", dot: "#3F8EBA" },
  { key: "ready", label: "De trimis", tone: "neutral", dot: "#965EA7" },
  { key: "scheduled", label: "Programat", tone: "neutral", dot: "#9CA3AF" },
];

export const CATEGORY_BY_KEY = Object.fromEntries(TODO_CATEGORIES.map((c) => [c.key, c]));

const dayKey = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(d);
const hhmm = (d) => new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, hour: "2-digit", minute: "2-digit" }).format(d);
const ddmm = (d) => new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, day: "2-digit", month: "2-digit" }).format(d);
const weekday = (d) => new Intl.DateTimeFormat("ro-RO", { timeZone: TZ, weekday: "long" }).format(d);

/** "azi la 14:20", "mâine la 08:00", "joi, 24.09 la 10:00". */
export function whenText(iso, now = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = dayKey(now);
  const tomorrow = dayKey(new Date(now.getTime() + 86_400_000));
  const k = dayKey(d);
  if (k === today) return `azi la ${hhmm(d)}`;
  if (k === tomorrow) return `mâine la ${hhmm(d)}`;
  return `${weekday(d)}, ${ddmm(d)} la ${hhmm(d)}`;
}

/** "40 h", "3 zile", "mai puțin de o oră". */
export function leftText(hours) {
  if (hours == null) return "";
  if (hours < 1) return "mai puțin de o oră";
  if (hours < 48) return `${Math.floor(hours)} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "o zi" : `${days} zile`;
}

const quote = (s) => `„${s}”`;

/** The one sentence at the top of a row: what is wrong and what to do. */
export function headline(item, now = new Date()) {
  const cat = item.was || item.category;
  const detail = item.detail || [];
  if (item.category === "contacted") {
    const c = item.contact || {};
    return `Contactat ${whenText(c.at, now)}${c.by ? ` de ${c.by}` : ""}: ${quote(c.note || "")}. Se așteaptă semnătura.`;
  }
  if (cat === "urgent") {
    const when = item.started
      ? `Grupa a început ${whenText(item.first_session_at, now)}.`
      : `Grupa începe în ${leftText(item.hours_left)}.`;
    if (item.state === "dossier_missing") return `${when} Dosarul MEC nu e completat, deci contractul nu poate pleca. Contactează participantul.`;
    if (item.state === "rejected") return `${when} Participantul a refuzat semnarea. Contactează-l.`;
    if (["cannot_generate", "send_failed", "dossier_refused"].includes(item.state)) return `${when} Contractul nu poate pleca. Rezolvă blocajul și contactează participantul.`;
    if (item.state === "ready") return `${when} Contractul nu a fost trimis. Trimite-l și anunță participantul.`;
    return `${when} Contractul nu e semnat. Contactează participantul să semneze.`;
  }
  if (cat === "rejected") {
    const reason = item.contract?.rejection_reason;
    return `${reason ? `A refuzat: ${quote(reason)}.` : "A refuzat semnarea."} Verifică datele și retrimite contractul.`;
  }
  if (cat === "blocked") return `Nu pleacă: ${detail[0] || "contractul nu poate fi generat."}`;
  if (cat === "no_reaction") return "2 reamintiri fără răspuns. Contactează participantul.";
  if (cat === "ready") return "Dosarul e complet, contractul nu a fost trimis încă. Trimite-l din înscriere sau prin trimiterea în lot.";
  if (cat === "scheduled") return nextStepText(item, now) || "Așteaptă semnătura. Nu mai e programată nicio reamintire automată.";
  return "";
}

/** The detail lines the headline has not said already. */
export function extraDetail(item) {
  const detail = item.detail || [];
  if (item.category === "rejected") return detail.filter((d) => d !== item.contract?.rejection_reason);
  if (item.category === "blocked") return detail.slice(1);
  if (item.category === "ready") return [];
  return detail;
}

/** "Reamintirea 2 pleacă joi, 24.09 la 14:20" / "Contractul pleacă mâine la 08:00". */
export function nextStepText(item, now = new Date()) {
  const s = item.next_step;
  if (!s) return "";
  if (s.kind === "reminder") return `Reamintirea ${s.step} pleacă ${whenText(s.at, now)}`;
  if (s.kind === "contract") {
    return `Contractul pleacă automat ${whenText(s.at, now)}${item.state === "send_failed" ? " (se reîncearcă)" : ""}`;
  }
  return "";
}

/** The switch and its pause, in one line for the top of "De rezolvat". */
export function autoSendText(auto, now = new Date()) {
  if (!auto) return "";
  if (!auto.enabled) return "Trimiterea automată a contractelor e oprită";
  const until = auto.pause?.until;
  if (until && new Date(until) > now) {
    return `Trimiterea automată e în pauză până ${whenText(until, now)}: ${auto.pause.reason || "eSemneaza a refuzat trimiterea"}`;
  }
  return "Trimiterea automată a contractelor e pornită";
}

/** Where the contract stands, in a few words. */
export function contractLine(item, now = new Date()) {
  const c = item.contract;
  if (!c || ["canceled"].includes(c.status)) {
    if (item.state === "dossier_missing") {
      return item.regime === "fped" ? "Fără contract · datele pentru contract necompletate"
        : "Fără contract · dosarul MEC necompletat";
    }
    return c ? `Ultimul contract (${c.number}) anulat` : "Fără contract";
  }
  const sent = c.sent_at ? ` · trimis ${whenText(c.sent_at, now)}` : "";
  const map = {
    sent: "nesemnat", rejected: "refuzat", send_failed: "netrimis", sending: "se trimite",
    participant_signed: "semnat de participant", completed: "semnat complet",
  };
  return `${c.number || "—"} · ${map[c.status] || c.status}${sent}`;
}

export function remindersLine(item, now = new Date()) {
  const c = item.contract;
  if (!c || c.status !== "sent") return "";
  if (!c.reminders) return "Nicio reamintire trimisă";
  const last = c.last_reminder_at ? `, ultima ${whenText(c.last_reminder_at, now)}${c.last_reminder_by === "automat" ? " (automată)" : ""}` : "";
  return `${c.reminders === 1 ? "O reamintire" : `${c.reminders} reamintiri`}${last}`;
}

/** Contract ids of urgent rows, to mark them in the register. */
export function urgentContractIds(items = []) {
  return new Set(items.filter((i) => (i.was || i.category) === "urgent" && i.contract?.id).map((i) => i.contract.id));
}

/**
 * The number on the "De rezolvat" tab: urgent cases in red; otherwise how
 * many cases there are, in grey; nothing when there is nothing to do.
 */
export function todoTabBadge(todo) {
  const counts = todo?.counts;
  if (!counts) return null;
  if (counts.urgent) return { count: counts.urgent, urgent: true, title: "Urgent: grupa începe în mai puțin de 48 h" };
  const total = (todo.items || []).length;
  return total ? { count: total, urgent: false, title: "Cazuri de rezolvat" } : null;
}
